<template>
  <aside class="ren-decoration-inspector ren-panel">
    <header class="ren-decoration-inspector__head">
      <h3>{{ title }}</h3>
    </header>

    <div class="ren-decoration-segmented" role="tablist" aria-label="检查对象">
      <button
        type="button"
        role="tab"
        :aria-selected="context === 'page'"
        class="ren-decoration-segmented__item"
        :class="{ 'is-active': context === 'page', active: context === 'page' }"
        @click="emit('update:context', 'page')"
      >
        页面
      </button>
      <button
        type="button"
        role="tab"
        :aria-selected="context === 'component'"
        class="ren-decoration-segmented__item"
        :class="{ 'is-active': context === 'component', active: context === 'component' }"
        @click="emit('update:context', 'component')"
      >
        组件
      </button>
    </div>

    <div v-if="context === 'component'" class="ren-decoration-subtabs" role="tablist" aria-label="组件属性分类">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        role="tab"
        :aria-selected="activeTab === tab.key"
        class="ren-decoration-subtabs__item"
        :class="{ 'is-active': activeTab === tab.key, active: activeTab === tab.key }"
        @click="emit('update:activeTab', tab.key)"
      >
        {{ tab.label }}
      </button>
    </div>

    <slot />
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export type DecorationInspectorTab = 'data' | 'style' | 'interaction'

const props = withDefaults(defineProps<{
  title?: string
  context?: 'page' | 'component'
  activeTab?: DecorationInspectorTab
  showDataSourceTab?: boolean
}>(), {
  title: '页面设置',
  context: 'page',
  activeTab: 'data',
  showDataSourceTab: false
})

const emit = defineEmits<{
  'update:context': [value: 'page' | 'component']
  'update:activeTab': [value: DecorationInspectorTab]
}>()

const tabs = computed(() => {
  const items: Array<{ key: DecorationInspectorTab, label: string }> = [
    { key: 'data', label: '内容' },
    { key: 'style', label: '样式' }
  ]
  if (props.showDataSourceTab) items.push({ key: 'interaction', label: '数据源' })
  return items
})
</script>

<style scoped>
.ren-decoration-inspector {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.ren-decoration-inspector__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.ren-decoration-inspector__head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--rr-color-text-strong, #0F172A);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 分段控制器 (Segmented Control) */
.ren-decoration-segmented {
  display: flex;
  align-items: center;
  padding: 3px;
  background: var(--rr-color-fill-quaternary, #F1F5F9);
  border-radius: var(--rr-radius-control, 8px);
  border: 1px solid var(--rr-color-border, #E2E8F0);
  gap: 2px;
  margin-bottom: 14px;
}

.ren-decoration-segmented__item {
  flex: 1 1 0%;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: calc(var(--rr-radius-control, 8px) - 3px);
  background: transparent;
  color: var(--rr-color-text-muted, #64748B);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  outline: none;
}

.ren-decoration-segmented__item:hover:not(.is-active) {
  color: var(--rr-color-text, #1E293B);
}

.ren-decoration-segmented__item.is-active {
  background: #FFFFFF;
  color: var(--rr-color-primary, #5A46E0);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
}

/* 二级 Tab 栏 (Subtabs) */
.ren-decoration-subtabs {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--rr-color-border, #E2E8F0);
  gap: 16px;
  margin-bottom: 14px;
}

.ren-decoration-subtabs__item {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 8px 2px;
  border: none;
  background: transparent;
  color: var(--rr-color-text-muted, #64748B);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease;
  outline: none;
}

.ren-decoration-subtabs__item:hover:not(.is-active) {
  color: var(--rr-color-text, #1E293B);
}

.ren-decoration-subtabs__item.is-active {
  color: var(--rr-color-primary, #5A46E0);
  font-weight: 600;
}

.ren-decoration-subtabs__item.is-active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--rr-color-primary, #5A46E0);
  border-radius: 1px;
}
</style>
